import * as logger from 'universal-logger';
import { call, takeLatest, takeEvery, spawn, put, all, select } from 'redux-saga/effects';
import { push as updatePath, replace as replacePath, LOCATION_CHANGE } from 'react-router-redux';
import { selectDistanceUnit } from 'redux/modules/locale';
import { isLoggedIn } from 'redux/modules/user';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import { SEARCH } from 'constants/filter-sets';
import { LoginRoute } from 'features/Login/Login.route';
import { reservationStatusChanged } from 'features/Reservation/Reservation.saga';
import { pathSelector, filterSetFilters, filterSetFilterValue } from 'redux/saga/selectors';
import { pick } from 'helpers/object';
import { timezoneDate } from 'helpers/time';
import { deepExtract } from 'helpers/deep';
import { ec } from 'helpers/multitrack';
import { segment } from 'helpers/segment';
import { isPresent } from 'helpers/verify';
import { scrollToTop } from 'redux/saga/helpers';
import { determineScheduleContext } from 'redux/saga/helpers/credit-cycle';
import { searchLayoutByName } from 'redux/saga/entities/bound';
import {
  selectSecondarySearchFilterWriteActions,
  searchFilterActions,
  selectSelectedVertical,
  selectSelectedCreditsRange,
} from './filters/filters.helpers';
import { SearchLocation } from './models/SearchLocation';
import {
  doLoadRadius,
  getResultType,
  getSelectedRadius,
  filterChanged,
  filtersChanged,
  TIME_DEFAULT,
  doSetupFilters,
} from './filters/filters.saga';
import { SEARCH_PATH } from './Search.route.path';
import {
  selectActiveLocation,
  selectDefaultLocation,
  selectKeywordSearchBoxText,
  selectLocationSearchBoxText,
  setLocationTimezone,
  selectSelectedKeywordId,
  selectSelectedKeywordType,
  selectSelectedLocation,
  setDefaultLocation,
  clearLocationField,
} from './SearchBox/SearchBox.reducer';
import {
  DEFAULT_GEOLOCATION,
  EVENTS,
  FILTER_NAMES,
  HEADER_LAYOUT,
  INITIAL_VISIBLE_VENUE_COUNT,
  RESULT_TYPES,
  RESULTS_LAYOUT,
  VERTICALS,
} from './Search.constants';
import {
  selectActiveUrl,
  selectCurrentPage,
  selectFilteringDisabled,
  selectMapView,
  selectReloadCursor,
  setActiveUrl,
  setCurrentPage,
  setFilteringDisabled,
  setReloadCursor,
  setSearchId,
  setVisibleVenues,
  TOGGLE_MAP_VIEW,
} from './Search.reducer';
import {
  getUserLocationForSearch,
  makeUrlFromLayout,
  selectCursorInLayout,
  selectFiltersFromSearchResults,
  selectKeywordAutocompleteEntryInLayout,
  selectLocationAutocompleteEntryInLayout,
  selectDataFromResultsLayout,
  selectSearchIdInLayout,
  selectVenuesInLayout,
  selectLocationAutocompleteTimezoneInLayout,
  selectResultTypeFromSearchResults,
  selectRadiusUsedInLayout,
  selectFiltersForHashInLayout,
  selectTotalHitsInLayout,
  selectMetadataInLayout,
} from './Search.helpers';
import { ITEM_TYPES } from './SearchBox/SearchBox.constants';
import { selectSearchBoxWriteActions } from './SearchBox/SearchBox.helpers';
import { getMapBoundsForViewport, doUpdateViewport } from './Map/SearchMap.saga';
import { doClearKeyword } from './filters/interactions.saga';
import { setMapLoaded, selectIsMapLoaded } from './Map/SearchMap.reducer';

const { ACTIVITY, AMENITY, DATE, RADIUS, RESULT_TYPE, TIME, TO_TIME, FROM_TIME, MAP_BOUNDS, LEVEL } = FILTER_NAMES;

export const loadPage = delta => ({ type: 'classpass/search/LOAD_PAGE', delta });
export const showAllVenues = () => ({ type: 'classpass/search/SHOW_ALL_VENUES' });
export const reloadSearch = () => ({ type: 'classpass/search/RELOAD_SEARCH' });

export const selectMapLoaded = action => action.type === setMapLoaded().type && action.value;

export const selectSearchLocationChange = action =>
  action.type === LOCATION_CHANGE && action.payload.pathname.indexOf(SEARCH_PATH) === 0;
export const selectIsToggleMapView = action => action.type === TOGGLE_MAP_VIEW;

export function* doTrackMapViewToggle() {
  const isMapView = yield select(selectMapView);

  if (isMapView) {
    yield call(segment.track, EVENTS.viewMap);
    yield call(ec, EVENTS.viewMap);
  }
}

export function* buildSearchRequestPayload() {
  const [filters, location, searchBoxKeywordId, searchBoxKeywordType, resultType, vertical, creditsRange] = yield all([
    select(filterSetFilters, SEARCH),
    select(selectActiveLocation),
    select(selectSelectedKeywordId),
    select(selectSelectedKeywordType),
    call(getResultType),
    select(selectSelectedVertical),
    select(selectSelectedCreditsRange),
  ]);

  const creditsRangeLow = creditsRange && creditsRange.low;
  const creditsRangeHigh = creditsRange && creditsRange.high;

  const params = {};
  if (searchBoxKeywordId) {
    if (!searchBoxKeywordType || searchBoxKeywordType === ITEM_TYPES.activity) {
      params.tag = [searchBoxKeywordId];
    } else if (searchBoxKeywordType === ITEM_TYPES.bookmarks) {
      params.bookmarks = true;
    }
  } else {
    params.tag = Object.keys(deepExtract(`${ACTIVITY}.itemIdSelected`, filters, {}));
  }

  const filterPayload = {
    ...(vertical !== VERTICALS.all && { vertical }), // To get results with all verticals we must omit the verticals key
    date: deepExtract(`${DATE}.value`, filters) || timezoneDate(deepExtract('timezone', location)),
    lat: location.lat,
    lon: location.lon,
    place_id: location.placeId,
    result_type: resultType,
    ...params,
    ...deepExtract(`${AMENITY}.itemIdSelected`, filters, {}),
    ...deepExtract(`${LEVEL}.itemIdSelected`, filters, {}),
    ...(isPresent(creditsRangeLow) && { min_credits: creditsRangeLow }),
    ...(isPresent(creditsRangeHigh) && { max_credits: creditsRangeHigh }),
  };

  if (location.mapBounds) {
    filterPayload.map_bounds = location.mapBounds;
  } else {
    const radius = getSelectedRadius(filters) || location.calculatedRadiusKm;
    if (radius) {
      const viewportBounds = yield call(getMapBoundsForViewport, location, radius);
      if (viewportBounds) {
        filterPayload.map_bounds = viewportBounds;
      } else {
        filterPayload.radius = radius;
      }
    }
  }

  const timeRange = deepExtract(`${TIME}.value`, filters);
  if (timeRange && timeRange !== TIME_DEFAULT) {
    const [ft, tt] = timeRange.split('-');
    filterPayload.ft = ft;
    filterPayload.tt = tt;
  }

  return { search_request: { filters: filterPayload } };
}

export function* resetFilters(filtersToOmit) {
  yield put(setFilteringDisabled(true));
  yield put(searchFilterActions.clearFilterSet(filtersToOmit));
  yield put(setFilteringDisabled(false));
}

export function* trackSearchQuery(result, filters = {}) {
  const keywordAutoCompleteEntry = selectKeywordAutocompleteEntryInLayout(result);
  const locationAutocompleteEntry = selectLocationAutocompleteEntryInLayout(result);

  const [resultType, keywordSearchBoxText, keywordSelectedId, locationSearchBoxText, totalHits] = yield all([
    select(filterSetFilterValue, SEARCH, RESULT_TYPE),
    select(selectKeywordSearchBoxText),
    select(selectSelectedKeywordId),
    select(selectLocationSearchBoxText),
    select(selectTotalHitsInLayout),
  ]);
  const mapBoundsFilter = filters[MAP_BOUNDS];
  const splitMapBounds = mapBoundsFilter && mapBoundsFilter.split(',');
  const searchId = selectSearchIdInLayout(result);
  const keywordHeaderType = keywordAutoCompleteEntry.type;
  const placeId = locationAutocompleteEntry.place_id;
  const centerLat = locationAutocompleteEntry.lat;
  const centerLon = locationAutocompleteEntry.lon;
  const [mapViewPortSouthWestLon, mapViewportSouthWestLat, mapViewportNorthEastLon, mapViewportNorthEastLat] =
    splitMapBounds || [];
  const {
    level_advanced: levelAdvance,
    level_all: levelAll,
    level_beginner: levelBeginner,
    locker,
    max_credits: maxCredits,
    min_credits: minCredits,
    parking,
    shower,
    vertical,
  } = filters;
  const filterSet = yield select(filterSetFilters, SEARCH);
  const radius = yield call(getSelectedRadius, filterSet);

  yield all([
    put(setSearchId(searchId)),
    call(segment.track, EVENTS.searchQuery, {
      activity: filters[ACTIVITY],
      distance_value: radius,
      ft: filters[FROM_TIME],
      keyword_id: keywordSelectedId,
      keyword_box_text: keywordSearchBoxText,
      keyword_type: keywordHeaderType,
      level_advanced: levelAdvance,
      level_all: levelAll,
      level_beginner: levelBeginner,
      location_box_text: locationSearchBoxText,
      locker,
      map_center_lat: centerLat,
      map_center_lon: centerLon,
      map_view_north_east_lat: mapViewportNorthEastLat,
      map_view_north_east_lon: mapViewportNorthEastLon,
      map_view_south_west_lat: mapViewportSouthWestLat,
      map_view_south_west_lon: mapViewPortSouthWestLon,
      max_credits: maxCredits,
      min_credits: minCredits,
      num_results: totalHits,
      page_type: SEARCH_PATH,
      parking,
      place_id: placeId,
      result_type: resultType,
      search_id: searchId,
      selection: JSON.stringify(filters),
      shower,
      tt: filters[TO_TIME],
      vertical,
    }),
    call(ec, EVENTS.searchQuery, {
      activity: filters[ACTIVITY],
      distance_value: radius,
      ft: filters[FROM_TIME],
      keyword_id: keywordSelectedId,
      keyword_box_text: keywordSearchBoxText,
      keyword_type: keywordHeaderType,
      level_advanced: levelAdvance,
      level_all: levelAll,
      level_beginner: levelBeginner,
      location_box_text: locationSearchBoxText,
      locker,
      map_center_lat: centerLat,
      map_center_lon: centerLon,
      map_view_north_east_lat: mapViewportNorthEastLat,
      map_view_north_east_lon: mapViewportNorthEastLon,
      map_view_south_west_lat: mapViewportSouthWestLat,
      map_view_south_west_lon: mapViewPortSouthWestLon,
      max_credits: maxCredits,
      min_credits: minCredits,
      num_results: totalHits,
      page_type: SEARCH_PATH,
      parking,
      place_id: placeId,
      result_type: resultType,
      search_id: searchId,
      selection: JSON.stringify(filters),
      shower,
      tt: filters[TO_TIME],
      vertical,
    }),
  ]);
}

export function* loadResults(client, page = 1, payload) {
  if (!payload && page === 1) {
    payload = yield call(buildSearchRequestPayload, client);
  } else if (!payload) {
    const previousPageData = yield select(selectDataFromResultsLayout, page - 1);
    const cursor = selectCursorInLayout(previousPageData);
    payload = {
      search_request: { cursor },
      modules: Object.keys(previousPageData), // fetch the same modules
    };
    yield put(setReloadCursor(cursor));
  }

  const distanceUnit = yield select(selectDistanceUnit);
  const [result] = yield all([
    call(fetchEntity, searchLayoutByName, [client, RESULTS_LAYOUT, payload, { distanceUnit }], {
      key: `${RESULTS_LAYOUT}_${page}`,
    }),
    put(setCurrentPage(page)),
  ]);

  const venues = (yield call(selectVenuesInLayout, result)) || [];
  yield put(setVisibleVenues(venues.slice(0, INITIAL_VISIBLE_VENUE_COUNT)));

  const searchFilters = yield select(filterSetFilters, SEARCH);
  const selectedRadiusId = deepExtract(`${RADIUS}.value`, searchFilters);
  if (!selectedRadiusId) {
    // if the radius is 'auto' we need to update the value in the radius filter select
    yield call(doLoadRadius);
  }

  const selectedLocation = yield select(selectSelectedLocation);
  const updatedLayout = yield select(selectDataFromResultsLayout);
  if (selectedLocation) {
    const currentTimezone = selectedLocation && selectedLocation.timezone;
    const timezoneFromLayout = yield call(selectLocationAutocompleteTimezoneInLayout, updatedLayout);

    if (!currentTimezone && timezoneFromLayout) {
      yield put(setLocationTimezone(timezoneFromLayout));
    }
  }

  // Determine the schedule context, so we display credits for the correct cycle
  yield call(determineScheduleContext, client, deepExtract(`${DATE}.value`, searchFilters));
  const filters = selectFiltersForHashInLayout(updatedLayout);
  yield spawn(trackSearchQuery, result, filters);

  return result;
}

export function* loadSearchHeader(client) {
  const { lat, lon } = yield select(getUserLocationForSearch);

  return yield call(fetchEntityIfNeeded, searchLayoutByName, [
    client,
    HEADER_LAYOUT,
    {
      header_request: { location: { lat, lon } },
    },
  ]);
}

export function* initializeUserLocationForSearch(client) {
  yield put(setFilteringDisabled(true));

  const [filters, selectedLocation, searchResultsData] = yield all([
    select(selectFiltersFromSearchResults),
    select(selectSelectedLocation),
    select(selectDataFromResultsLayout),
  ]);
  const locationAutocompleteEntry = selectLocationAutocompleteEntryInLayout(searchResultsData);

  if (selectedLocation) {
    yield put(clearLocationField());
  }

  let defaultLocation;
  const { lat, lon } =
    locationAutocompleteEntry.lat && locationAutocompleteEntry.lon ? locationAutocompleteEntry : filters;
  if (lat && lon) {
    // Sets default location using the search layout: RESULTS_LAYOUT
    const radius = selectRadiusUsedInLayout(searchResultsData);

    defaultLocation = new SearchLocation({
      lat,
      lon,
      calculated_radius_km: radius,
      ...pick(['name', 'place_id', 'timezone'], locationAutocompleteEntry),
    });
  } else {
    // Sets default location using the search layout: HEADER_LAYOUT
    const searchHeaderData = yield call(loadSearchHeader, client);
    const headerLocation = deepExtract('location_history.data.default_location', searchHeaderData, DEFAULT_GEOLOCATION);
    defaultLocation = new SearchLocation(headerLocation);
  }

  yield put(setDefaultLocation(defaultLocation));
  yield put(setFilteringDisabled(false));
}

export function* reloadResults(client) {
  const currentPage = yield select(selectCurrentPage);
  let payload;

  if (currentPage > 1) {
    const previousPageData = yield select(selectDataFromResultsLayout, currentPage);
    const cursor = yield select(selectReloadCursor);
    payload = {
      search_request: { cursor },
      modules: Object.keys(previousPageData),
    };
  }

  yield call(loadResults, client, currentPage, payload);
}

export function* loadResultsFromUrl(client, url) {
  const [existingFilters, location] = yield all([select(filterSetFilters, SEARCH), select(selectActiveLocation)]);

  // TODO: Consider ways we can get filters directly using the url hash instead of requesting the search layout first
  //       This will allow us to make all requests in the same way and not need to call `reloadResults` to make sure
  //       we get results for the users viewport.  Captured in SEARCH-4648.
  const result = yield call(loadResults, client, 1, {
    search_request: {
      hash: url,
      filters: {
        date: deepExtract(`${DATE}.value`, existingFilters) || timezoneDate(deepExtract('timezone', location)),
      },
    },
  });

  yield call(initializeUserLocationForSearch, client);
  yield call(doSetupFilters);
  const urlFromResults = makeUrlFromLayout(result);
  yield put(setActiveUrl(urlFromResults));

  const isMapLoaded = yield select(selectIsMapLoaded);
  // If the map is loaded send a new request to get results for users viewport
  if (isMapLoaded) {
    yield call(reloadResults, client);
  }

  return result;
}

export function* doFilterChanged(client, action) {
  try {
    // If we are currently ignoring change events, abort
    if (yield select(selectFilteringDisabled)) {
      return;
    }
    const result = yield call(loadResults, client, 1);

    const searchUrl = makeUrlFromLayout(result);

    if (searchUrl) {
      yield all([put(setActiveUrl(searchUrl)), put(updatePath(searchUrl))]);
    }

    const actionData = action && (action.data || action);
    const { filter: filterName, filters: filterNames } = actionData;
    const allFilterNames = filterNames || (filterName && [filterName]);

    if (allFilterNames) {
      yield put(searchFilterActions.resetNeedsUpdate(allFilterNames));
    }
  } catch (err) {
    logger.error('Failed while applying filters', err);
  }
}

export function* loadResultsForUrlIfNeeded(client, url) {
  const urlHash = url
    .split('/')
    .filter(Boolean)
    .pop();
  const currentSearchLayoutData = yield select(selectDataFromResultsLayout);
  const { hash: currentResultsHash } = currentSearchLayoutData ? selectMetadataInLayout(currentSearchLayoutData) : {};

  if (!currentSearchLayoutData || currentResultsHash !== urlHash) {
    yield call(loadResultsFromUrl, client, url);
  }
}

export function* handleUrlChange(client, action) {
  const newUrl = action.payload.pathname;
  const activeUrl = yield select(selectActiveUrl);

  // If we have already loaded the data for this URL we can ignore this change event.
  if (newUrl === activeUrl) {
    return;
  }

  yield call(doClearKeyword, true);
  if (newUrl === SEARCH_PATH) {
    yield put(setActiveUrl(SEARCH_PATH));
    yield call(resetFilters); // ensure no filters are set when navigating to the base search path
    yield call(loadResults, client);
  } else {
    yield call(loadResultsForUrlIfNeeded, client, newUrl);
  }
}

export function* handleReservationStatusChanged(client) {
  yield call(reloadResults, client);
}

export function* handlePageChange(client, { delta }) {
  try {
    const currentPage = yield select(selectCurrentPage);
    const nextPage = currentPage + delta;
    yield all([call(scrollToTop), call(loadResults, client, nextPage)]);
  } catch (err) {
    logger.error('Failed while changing page', err);
  }
}

export function* hasLocationBeenSet() {
  const [hasDefaultLocation, hasSelectedLocation] = yield all([
    select(selectDefaultLocation),
    select(selectSelectedLocation),
  ]);

  return !!hasDefaultLocation || !!hasSelectedLocation;
}

export function* loadSearch(client) {
  try {
    const url = yield select(pathSelector);

    if (url !== SEARCH_PATH) {
      yield call(loadResultsForUrlIfNeeded, client, url);

      const searchResultType = yield select(selectResultTypeFromSearchResults);
      if (searchResultType && searchResultType === RESULT_TYPES.SCHEDULE) {
        const loggedIn = yield select(isLoggedIn);
        if (!loggedIn) {
          return yield put(replacePath(`${LoginRoute().path}/?returnTo=${encodeURIComponent(url)}`));
        }
      }

      const correctUrl = yield select(selectActiveUrl);
      if (correctUrl && url !== correctUrl) {
        return yield put(
          replacePath({
            pathname: correctUrl,
            state: { status: 301 },
          })
        );
      }
    } else {
      yield call(initializeUserLocationForSearch, client);

      const currentSearchLayoutData = yield select(selectDataFromResultsLayout);
      if (!currentSearchLayoutData) {
        yield call(loadResults, client, 1);
      }
      yield call(doSetupFilters);
    }
  } catch (err) {
    logger.error('Error loading search', err);
  }
}

export function* doShowAllVenues() {
  const results = yield select(selectDataFromResultsLayout);
  const venues = yield call(selectVenuesInLayout, results);
  yield put(setVisibleVenues(venues));
}

export function* doHandleMapLoaded(client, action) {
  try {
    yield call(doUpdateViewport, {
      mapDimension: {
        width: action.options.width,
        height: action.options.height,
      },
    });
    yield call(reloadResults, client);
  } catch (err) {
    logger.error('Error while handling map load', err);
  }
}

export function* watchSearch(client) {
  yield all([
    takeEvery(selectIsToggleMapView, doTrackMapViewToggle),
    takeLatest(reservationStatusChanged().type, handleReservationStatusChanged, client),
    takeLatest(loadPage().type, handlePageChange, client),
    takeLatest(showAllVenues().type, doShowAllVenues),
    takeLatest(selectSearchLocationChange, handleUrlChange, client),
    takeLatest(reloadSearch().type, reloadResults, client),
    takeLatest(
      [
        selectSecondarySearchFilterWriteActions,
        filterChanged().type,
        filtersChanged().type,
        selectSearchBoxWriteActions,
      ],
      doFilterChanged,
      client
    ),
    takeLatest(selectMapLoaded, doHandleMapLoaded, client),
  ]);
}
